// P-ONBOARD-A — Behavior-anchored parent abilities survey (READING half).
//
// Replaces the T1.1a vague-checkbox survey ("Reads simple sentences") with
// research-authored behavior-anchored questions ("Can your child read a
// very short sentence like I see a dog?") + a 4-way response scale
// (Usually on their own / Sometimes with help / Not yet / Not sure).
//
// LAYOUT (B per the user's recon rule "kid onboarding is strongly step-by-
// step, so use two screens"): this route shows the 5 READING questions;
// the math half lives at /onboarding/kid/abilities-math. Both screens
// share one logical step ("Step 4 of 6") — the math screen reuses the
// same kidStepProgress("abilities") call so the counter doesn't bump
// mid-survey.
//
// RESPONSE SEMANTICS (engine consumption unchanged; see parentPriors.ts):
//   - "Usually on their own" sets <slug>: true. The engine's existing
//     tickedAbilitySlugs(`=== true`) rule treats it as a ticked ability
//     and walks the prerequisite graph from the anchor (no behaviour
//     change vs T1.1a).
//   - All other responses set <slug>: false. The engine sees no signal
//     today (treated like a skipped/missing answer).
//   - "Not sure" is INTENTIONALLY treated like missing — per the research
//     spec, low-confidence responses should defer to first-session
//     calibration rather than push an unreliable prior into the engine.
//   - The full 4-way response is ALSO persisted as
//     <slug>_confidence: "usually"|"sometimes"|"not_yet"|"unsure" so
//     P-ONBOARD-B's band-shift calibration can read it without an
//     onboarding rerun.
//
// OPTIONAL by design (unchanged): "Continue →" is always enabled. Skipping
// the survey means the engine falls back to the age-based default.

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useOnboardingDraft } from "@/lib/onboardingDraft";
import { kidStepProgress } from "@/lib/kidOnboarding";
import {
  ABILITY_OPTIONS,
  responseToPrior,
  confidenceFor,
} from "@/lib/engine/parentPriors";
import type { ParentPriors, AbilityResponse } from "@/App/progress";

export const Route = createFileRoute("/onboarding/kid/abilities")({
  head: () => ({ meta: [{ title: "PencilWise · Reading check-in" }] }),
  component: KidAbilitiesReading,
});

// The 4 response chips, in render order. Each maps via responseToPrior to
// (value=bool, confidence=AbilityResponse) on persist.
const RESPONSES: { value: AbilityResponse; label: string }[] = [
  { value: "usually", label: "Usually on their own" },
  { value: "sometimes", label: "Sometimes with help" },
  { value: "not_yet", label: "Not yet" },
  { value: "unsure", label: "Not sure" },
];

// H-A4 shared step styles (H-0 tokens) — see onboarding.kid.name.tsx.
// Each question is a cream-deep card; chips sit on bg-background (cream)
// inside it so the layering reads card → chip. The legend keeps its
// fieldset-direct-child position (a11y group label) via the float reset.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";

function KidAbilitiesReading() {
  const navigate = useNavigate();
  const { draft, setDraft } = useOnboardingDraft();
  const { current, total } = kidStepProgress("abilities");
  const name = draft.name?.trim() || "your child";

  // Seed from the draft so back-navigation (from math screen) preserves
  // the previous answers. Reads the existing confidence sibling first,
  // falling back to legacy `<slug>: true` records via confidenceFor.
  const readingSlugs = ABILITY_OPTIONS.filter((o) => o.group === "reading");
  const [picked, setPicked] = useState<Record<string, AbilityResponse>>(() => {
    const out: Record<string, AbilityResponse> = {};
    for (const opt of readingSlugs) {
      const c = confidenceFor(draft.parentPriors, opt.slug);
      if (c) out[opt.slug] = c;
    }
    return out;
  });

  const choose = (slug: string, response: AbilityResponse) =>
    setPicked((prev) => ({ ...prev, [slug]: response }));

  const submit = () => {
    // Merge picked responses into the EXISTING draft.parentPriors so the
    // math half (already-answered) isn't dropped on re-edit / back-nav.
    const next: ParentPriors = { ...(draft.parentPriors ?? {}) };
    for (const opt of readingSlugs) {
      const response = picked[opt.slug];
      if (!response) {
        // Skipped — drop the slug entirely (no signal, engine falls back).
        delete next[opt.slug];
        delete next[`${opt.slug}_confidence`];
        continue;
      }
      const entry = responseToPrior(response);
      next[opt.slug] = entry.value;
      next[`${opt.slug}_confidence`] = entry.confidence;
    }
    setDraft({ parentPriors: next });
    navigate({ to: "/onboarding/kid/abilities-math", search: (prev) => prev });
  };

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>
            Step {current} of {total} · Reading
          </p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            What can {name} do with reading?
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Pick what fits best — &quot;Not sure&quot; is fine. We&apos;ll calibrate
            in the first session.
          </p>
        </header>

        <div className="mt-6 space-y-4">
          {readingSlugs.map((o) => (
            <fieldset
              key={o.slug}
              data-slug={o.slug}
              className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft"
            >
              <legend className="float-left text-sm font-bold leading-snug text-foreground">
                {o.label}
              </legend>
              <div
                className="clear-both grid grid-cols-1 gap-2 pt-3"
                role="radiogroup"
                aria-label={o.label}
              >
                {RESPONSES.map((r) => {
                  const on = picked[o.slug] === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      data-response={r.value}
                      onClick={() => choose(o.slug, r.value)}
                      className={`flex min-h-[48px] w-full items-center gap-3 rounded-xl border-2 px-4 py-2 text-left text-sm font-bold transition-colors ${
                        on
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border bg-background text-foreground hover:border-primary"
                      }`}
                    >
                      <span
                        className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 text-xs ${
                          on
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-ink/40 bg-transparent"
                        }`}
                        aria-hidden="true"
                      >
                        {on ? "✓" : ""}
                      </span>
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <button onClick={submit} className={`mt-6 ${CTA}`}>
          Continue →
        </button>
      </div>
    </div>
  );
}
